import { Icon } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomDialog from "components/Atoms/CustomDialog"
import Note from "components/Atoms/Note"
import dayjs from "dayjs"
import advancedFormat from "dayjs/plugin/advancedFormat"
import { usePreTripGenerateLazyQuery } from "generated/graphql"
import {
  useGenerateTrips,
  useScheduleListSummary,
} from "graphql/scheduling/scheduling.hooks"
import useAuth from "hooks/useAuth"
import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { useGenerateTripStore } from "store/generateTripStore"
import { getId } from "utils/custom.utils"
import useSchedulingQuery from "../useSchedulingQuery"
import TripsAlertTable from "./TripsAlertTable"
dayjs.extend(advancedFormat)
const GenerateTripDialog = ({ isOpen, schedules, validSchedules, onClose }) => {
  const {
    user: { currentDepartment },
  } = useAuth()
  const summary = useScheduleListSummary()
  const generatableSchedules = summary?.count ?? 0
  const [generateTrips, { loading }] = useGenerateTrips(() => {
    onClose()
  })
  const date = useSelector(state => state.scheduling.selectedDate)
  const { data } = useSchedulingQuery({ unpaginated: true })
  const latestError = useGenerateTripStore(store => store?.latestError)
  const setSchedules = useGenerateTripStore(store => store.setSchedules)
  const parsedSchedules = useGenerateTripStore(store => store.parsedSchedules())
  const [getPreTripData, { data: preTripData }] = usePreTripGenerateLazyQuery({
    variables: {
      date: date,
      scheduleIds: parsedSchedules?.map(schedule => {
        return schedule?.schedule
      }),
    },
  })
  const [noteIsOpen, setNoteIsOpen] = useState(true)

  useEffect(() => {
    setSchedules(
      // If schedules are selected (by clicking the checkbox) only consider the selected ones
      // Also store the index here since the order might get lost when performing operations
      validSchedules?.length > 0
        ? validSchedules
            ?.map((sch, idx) => {
              const schedule = data?.schedulesForDate?.find(
                s => getId(s) === getId(sch)
              )
              if (!schedule) return null
              return {
                ...schedule,
                procuredQuantity: schedule.requiredQuantity,
                index: idx,
              }
            })
            ?.filter(sch => sch)
        : // Else take all schedules into consideration
          // Filter out all the notified schedules (whose has been trip generated already)
          // Store the index to preserve order!
          data?.schedulesForDate
            ?.filter(sch => !sch.isNotified)
            ?.map((sch, idx) => ({
              ...sch,
              procuredQuantity: sch.requiredQuantity,
              index: idx,
            }))
    )
  }, [validSchedules, data?.schedulesForDate, setSchedules])

  function renderFeedbackText() {
    // If there are no validSchedules then that means that the trips are already generated!
    if (schedules?.length > 0 && validSchedules?.length === 0) {
      return "Trips are already generated for the selected schedules!"
    }
    // If there are errors show the errors
    if (latestError) {
      return (
        <>
          Please fill all the missing (<Icon intent="danger" icon="cross" />)
          values values before continuing
        </>
      )
    }

    if (!parsedSchedules?.length) {
      return <>Please select schedules before proceeding.</>
    }
    return null
  }
  return (
    <CustomDialog
      light
      width={"70%"}
      scrollable={false}
      className="vt-generate-trip-dialog bg-gray-100"
      icon="known-vehicle"
      onOpened={() => {
        getPreTripData()
      }}
      title={
        <span>
          Generate Trips |{" "}
          <span className="text-gray-600">
            {dayjs(date).format("Do MMM YYYY")}
          </span>
        </span>
      }
      isOpen={isOpen}
      onClose={() => {
        onClose()
        setNoteIsOpen(true)
      }}
      footerRightButton={
        <CustomButton
          dark
          loading={loading}
          disabled={latestError || !parsedSchedules?.length ? true : false}
          icon="endorsed"
          text="Generate Trips"
          onClick={() => {
            generateTrips({
              variables: {
                date,
                // schedules: validSchedules ? validSchedules : null,
                schedulesProcured: parsedSchedules,
              },
            })
          }}
        />
      }
    >
      {preTripData?.preTripGenerate.length > 0 ? (
        <div className="mb-1 text-base">
          <p>
            {" "}
            There are {generatableSchedules} schedules created across{" "}
            {Math.ceil(parsedSchedules?.length / 10)} pages
          </p>
          <br />
          <p>
            Are you sure you want to generate{" "}
            <span className="font-700">
              {preTripData?.preTripGenerate?.length} trips
            </span>{" "}
            for
            <p className="text-xl">{dayjs(date).format("Do MMMM YYYY")}?</p>
          </p>
          {noteIsOpen && (
            <Note
              intent="warning"
              className="max-w-full mb-3 text-left text-sm items-start"
              onClear={() => {
                setNoteIsOpen(!noteIsOpen)
              }}
            >
              <Icon icon="warning-sign" className="mr-2" />
              <span className="font-600">Trip Creation</span>
              <p className="pl-6">
                A Trip is created when all bookings have the same aggregator and
                vehicle number. Also if bookings are scheduled in one vehicle
                where inward has already started and data has been "confirmed",
                this schedule will be treated as a new trip.
              </p>
            </Note>
          )}
        </div>
      ) : (
        ""
      )}
      <p className="text-base mb-3">{renderFeedbackText()}</p>
      {/* If there are errors in the list of schedules display the alert table */}
      {/* {errorDetails && ( */}
      {<TripsAlertTable />}
      {currentDepartment?.generatePoAfterInward ? null : (
        <p className="mt-3 text-center vt-warning">
          * On Clicking the 'Generate Trips' button, Purchase Orders(POs) will
          be created
        </p>
      )}
    </CustomDialog>
  )
}

export default GenerateTripDialog
